import { useState, useEffect, useMemo } from "react";
import PageLayout from "../../../Layouts/PageLayout/PageLayout";
import BarChartGraph from "../../../components/common/Charts/BarChartGraph";
import GasesLineChartGraph from "../../../components/common/Charts/GasesLineChartGraph";
import Table from "../../../components/common/Table/Table";
import Button from "../../../components/common/Button/Button";
import Modal from "../../../components/common/Modal/Modal";
import { getApiInstance } from "../../../utils/api";
import { FiDownload } from "react-icons/fi";
import { MdOutlineFileDownload } from "react-icons/md";
import { FilterBar } from "../../../components/filtersNew/filter-barNew";
import ExcluirRaeeZeroWasteToggle, { filtrarProyectosExcluidos } from "../../../components/common/Inputs/ExcluirRaeeZeroWasteToggle/ExcluirRaeeZeroWasteToggle";
import {
    createFilterState,
    applyFilterState,
} from "../../../lib/reportNew/filter-stateNew";
import {
    useExcelExport,
    buildInformeExportFilters,
    codsFilaDe,
} from "../useExcelExport";
import "../cantidadesGeneradas/CantidadesGeneradas.scss";
import "./HuellaCarbono.scss";
import TabsNavigation from "../../../components/common/TabsNavigation/TabsNavigation";

const COLOR_CO2 = "#2563eb";
const COLOR_CH4 = "#8b5cf6";
const COLOR_N2O = "#10b981";

const MESES = [
    "Ene",
    "Feb",
    "Mar",
    "Abr",
    "May",
    "Jun",
    "Jul",
    "Ago",
    "Sep",
    "Oct",
    "Nov",
    "Dic",
];

const normalizeCadena = (nombre) =>
    String(nombre ?? "").trim().toLowerCase();

export default function HuellaCarbono() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [informeData, setInformeData] = useState([]);
    const [activeTab, setActiveTab] = useState("grafica");
    const [cadenasCatalogo, setCadenasCatalogo] = useState([]);

    const filterFields = [
        { key: "anio", label: "Año", type: "enum" },
        {
            key: "codUbicacion",
            label: "Ubicación",
            type: "enum",
            labelMap: ["", "Calle", "Centro comercial"],
        },
        { key: "intermediario", label: "Intermediario", type: "enum" },
        { key: "provincia", label: "Provincia", type: "enum" },
    ];

    const defaultQuickFilters = ["anio", "codUbicacion", "intermediario", "provincia"];

    const [filterState, setFilterState] = useState(() =>
        createFilterState(defaultQuickFilters)
    );

    const { isModalExcelOpen, openModal, closeModal, handleExportToExcel, handleExportInformeCompleto, exportando } =
                useExcelExport({
                    onError: setError,
                    resumenUrl: "api/Informe/CreateReportExcelHuellaResumen",
                });

    useEffect(() => {
        const fetchHuellaCarbono = async () => {
            setLoading(true);
            try {
                const api = getApiInstance();
                const response = await api.get(`/api/Informe/GetHuella`);
                setInformeData(response.data ?? []);
                setError(null);
            } catch (err) {
                console.error(err);
                setError("Error al cargar los datos.");
            } finally {
                setLoading(false);
            }
        };
        fetchHuellaCarbono();
    }, []);

    /* Cadenas reales del cliente (tabla Cadenas), igual que en Centros de
       producción y Obras menores: nunca hardcodeadas aquí. */
    useEffect(() => {
        const fetchCadenas = async () => {
            try {
                const api = getApiInstance();
                const response = await api.get(`/api/Cadenas/GetAllCadenas`);
                const nombres = (response.data ?? []).map((c) => c.cadena);
                setCadenasCatalogo([...nombres, "Edificio singular"]);
            } catch (err) {
                console.error(err);
            }
        };
        fetchCadenas();
    }, []);

    const [excluirRaeeZeroWaste, setExcluirRaeeZeroWaste] = useState(false);

    const { datosFiltrados } = useMemo(() => {
        return {
            datosFiltrados: filtrarProyectosExcluidos(
                applyFilterState(
                    informeData,
                    filterState,
                    filterFields
                ),
                excluirRaeeZeroWaste
            ),
        };
    }, [informeData, filterState, filterFields, excluirRaeeZeroWaste]);

    const exportFilters = useMemo(() => {
        const quick = filterState.quick || {};
        const anios = Array.isArray(quick.anio) ? quick.anio : [];
        return {
            ...buildInformeExportFilters({
                selectedAnio: anios.length ? anios[0] : "all",
                selectedZonas: quick.codZona,
            }),
            /* Lo que manda de verdad: las filas que se están viendo. El export
               no tenía ningún filtro, así que hasta ahora salía el informe entero. */
            codsFila: codsFilaDe(datosFiltrados),
        };
    }, [filterState, datosFiltrados]);

    const emisionesMensuales = useMemo(() => {
        const mensual = MESES.map((name) => ({ name, co2: 0, ch4: 0, n2o: 0 }));
        datosFiltrados.forEach((r) => {
            const idx = Number(r.mes) - 1;
            if (idx >= 0 && idx < 12) {
                mensual[idx].co2 += Number(r.emisionesCO2 ?? 0);
                mensual[idx].ch4 += Number(r.emisionesCH4 ?? 0);
                mensual[idx].n2o += Number(r.emisionesN2O ?? 0);
            }
        });
        return mensual;
    }, [datosFiltrados]);

    const datosPorCadenaCompleto = useMemo(() => {
        const mapa = new Map(
            cadenasCatalogo.map((nombre) => [
                normalizeCadena(nombre),
                {
                    categoria: nombre,
                    emisionesCO2: 0,
                    emisionesCH4: 0,
                    emisionesN2O: 0,
                    emisionesTotales: 0,
                },
            ])
        );
        datosFiltrados.forEach((r) => {
            const nombre = r.cadena || "Sin Asignar";
            const clave = normalizeCadena(nombre);
            const item =
                mapa.get(clave) ??
                {
                    categoria: nombre,
                    emisionesCO2: 0,
                    emisionesCH4: 0,
                    emisionesN2O: 0,
                    emisionesTotales: 0,
                };
            item.emisionesCO2 += Number(r.emisionesCO2 ?? 0);
            item.emisionesCH4 += Number(r.emisionesCH4 ?? 0);
            item.emisionesN2O += Number(r.emisionesN2O ?? 0);
            item.emisionesTotales += Number(r.emisionesTotales ?? 0);
            mapa.set(clave, item);
        });
        return [...mapa.values()].sort(
            (a, b) => b.emisionesTotales - a.emisionesTotales
        );
    }, [datosFiltrados, cadenasCatalogo]);

    const graficoPorCadena = useMemo(
        () =>
            datosPorCadenaCompleto.map((item) => ({
                name: item.categoria,
                "CO2 (kg)": parseFloat(item.emisionesCO2.toFixed(2)),
                "CH4 (g)": parseFloat(item.emisionesCH4.toFixed(2)),
                "N2O (g)": parseFloat(item.emisionesN2O.toFixed(2)),
            })),
        [datosPorCadenaCompleto]
    );

    const columnasTabla = [
        {
            key: "categoria",
            label: "Cadena",
            visible: true,
            sortable: true,
        },
        {
            key: "emisionesCO2",
            label: "Emisiones CO2 (kg)",
            type: "number",
            visible: true,
            sortable: true,
            render: (row) => row.emisionesCO2.toFixed(2),
        },
        {
            key: "emisionesCH4",
            label: "Emisiones CH4 (g)",
            type: "number",
            visible: true,
            sortable: true,
            render: (row) => row.emisionesCH4.toFixed(2),
        },
        {
            key: "emisionesN2O",
            label: "Emisiones N2O (g)",
            type: "number",
            visible: true,
            sortable: true,
            render: (row) => row.emisionesN2O.toFixed(2),
        },
        {
            key: "emisionesTotales",
            label: "Emisiones Totales (kg)",
            type: "number",
            visible: true,
            sortable: true,
            render: (row) => row.emisionesTotales.toFixed(2),
        },
    ];

    const tabs = [
        { id: "grafica", label: "Gráfica" },
        { id: "desglose", label: "Desglose" },
        { id: "listado", label: "Listado" },
    ];

    const renderGraficaContent = () => (
        <div className="cantidades-generadas-content">
            <div className="graficas-card">
                <div className="charts-section">
                    <div className="chart-container bar-chart-container">
                        <div className="chart-header">
                            <h3>Emisión de gases a la atmósfera</h3>
                        </div>
                        <div className="chart-wrapper">
                            <GasesLineChartGraph data={emisionesMensuales} />
                        </div>
                        <div className="gases-legend">
                            <div className="gases-legend__item">
                                <span
                                    className="gases-legend__dot"
                                    style={{ background: COLOR_CO2 }}
                                />
                                <span className="gases-legend__label">
                                    Emisiones parciales kg CO2
                                </span>
                            </div>
                            <div className="gases-legend__item">
                                <span
                                    className="gases-legend__dot"
                                    style={{ background: COLOR_CH4 }}
                                />
                                <span className="gases-legend__label">
                                    Emisiones parciales g CH4
                                </span>
                            </div>
                            <div className="gases-legend__item">
                                <span
                                    className="gases-legend__dot"
                                    style={{ background: COLOR_N2O }}
                                />
                                <span className="gases-legend__label">
                                    Emisiones parciales g N2O
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderDesgloseContent = () => (
        <div className="cantidades-generadas-content">
            <div className="graficas-card">
                <div className="charts-section">
                    <div className="chart-container bar-chart-container">
                        <div className="chart-header">
                            <h3>Emisiones por cadena</h3>
                        </div>
                        <div className="chart-wrapper">
                            <BarChartGraph
                                data={{ all: graficoPorCadena }}
                                activeTab="all"
                                names={{
                                    "CO2 (kg)": "Emisiones CO2 (kg)",
                                    "CH4 (g)": "Emisiones CH4 (g)",
                                    "N2O (g)": "Emisiones N2O (g)",
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderListadoContent = () => (
        <div className="listado-content">
            <Table
                columns={columnasTabla}
                dataTable={datosPorCadenaCompleto}
                keyProp="categoria"
                sortProp="categoria"
                searchTerms={[
                    "categoria",
                    "emisionesCO2",
                    "emisionesCH4",
                    "emisionesN2O",
                    "emisionesTotales",
                ]}
                noResultsMessage="No hay datos para el año seleccionado."
                elementName="cadena"
            />
        </div>
    );

    return (
        <div className="huella-carbono-page">
            <PageLayout
                title="Informe de Huella de Carbono"
                subtitle="Análisis de emisiones de CO2, CH4 y N2O"
                actions={[
                    <Button
                        key="export"
                        componentType="button"
                        variant="filled"
                        startIcon={<FiDownload />}
                        onClick={openModal}
                    >
                        Exportar a excel
                    </Button>,
                ]}
            >
                <div className="filters-section-common">
                    <FilterBar
                        rows={informeData}
                        state={filterState}
                        onChange={setFilterState}
                        resultCount={datosFiltrados.length}
                        totalCount={informeData.length}
                        fields={filterFields}
                        headerExtra={
                            <ExcluirRaeeZeroWasteToggle
                                activo={excluirRaeeZeroWaste}
                                onChange={setExcluirRaeeZeroWaste}
                            />
                        }
                    />
                </div>

                <TabsNavigation
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                />

                {error && <div className="error-message">{error}</div>}

                <Modal
                    isOpen={isModalExcelOpen}
                    onClose={closeModal}
                    title="Exportar informe a Excel"
                    actions={[
                        <Button
                            key="export-resumen"
                            className="export-excel__btn export-excel__btn--primary"
                            onClick={() =>
                                handleExportToExcel("resumen", exportFilters)
                            }
                            startIcon={<MdOutlineFileDownload />}
                            disabled={exportando}
                        >
                            {exportando === "resumen"
                                ? "Generando..."
                                : "Informe Huella de Carbono"}
                        </Button>,
                        <Button
                            key="export-completo"
                            className="export-excel__btn export-excel__btn--secondary"
                            onClick={() =>
                                handleExportInformeCompleto({ filterState, excluirRaeeZeroWaste })
                            }
                            startIcon={<MdOutlineFileDownload />}
                            disabled={exportando}
                        >
                            {exportando === "completo"
                                ? "Generando..."
                                : "Informe Completo"}
                        </Button>,
                    ]}
                >
                    <div className="export-excel__actions"></div>
            </Modal>


                {loading ? (
                    activeTab === "listado" ? (
                        <div
                            className="informe-table-skeleton"
                            aria-hidden="true"
                        >
                            <div className="informe-table-skeleton__row" />
                            <div className="informe-table-skeleton__row" />
                            <div className="informe-table-skeleton__row" />
                            <div className="informe-table-skeleton__row" />
                        </div>
                    ) : (
                        <div
                            className="informe-chart-skeleton informe-chart-skeleton--bar"
                            aria-hidden="true"
                        >
                            <div className="informe-chart-skeleton__bars">
                                {[80, 55, 70, 40, 62, 30, 48].map(
                                    (h, i) => (
                                        <div
                                            key={i}
                                            className="informe-chart-skeleton__bar"
                                            style={{ height: `${h}%` }}
                                        />
                                    )
                                )}
                            </div>
                        </div>
                    )
                ) : activeTab === "grafica" ? (
                    renderGraficaContent()
                ) : activeTab === "desglose" ? (
                    renderDesgloseContent()
                ) : (
                    renderListadoContent()
                )}
            </PageLayout>
        </div>
    );
}
